import type { ReactNode } from 'react';

import type { IconNames } from '../../../../uniform/components/text-with-icon/src/icon-mapping';
import type { Meter } from '../context/types';

export type BulletOrIconListItem = {
	icon?: IconNames;
	text: string | ReactNode;
};

export interface FormatPriceOptions {
	currency: string;
	locale: string;
	fractionDigits?: number;
}

/**
 * Customer-facing meter display names.
 *
 * Source of truth: per-meter Confluence content specs (each spec has a
 * "Meter name" row on the Step 1 table). Any change to these strings
 * must be signed off by the meter's Content DRI.
 *
 * NOTE: These are the DISPLAY labels shown to users. The internal enum
 * key (`Meter`) is kept as a stable identifier used in URLs, analytics,
 * and context state — do NOT rename the keys.
 */
export const METER_LABELS: Record<Meter, string> = {
	rovoCredits: 'Rovo credits',
	automations: 'Automation steps',
	csResolutions: 'AI agent resolutions',
	assetsObjects: 'Assets objects',
};

/**
 * Formats a large integer for display (e.g. usage / allowance counts):
 * 145000 → "145,000" (using the caller's locale).
 */
export const formatNumber = (value: number, locale: string): string =>
	new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);

export const formatPrice = (price: number, options: FormatPriceOptions): string => {
	const { currency, locale, fractionDigits = 0 } = options;
	return new Intl.NumberFormat(locale, {
		style: 'currency',
		currency,
		minimumFractionDigits: fractionDigits,
		maximumFractionDigits: fractionDigits,
	}).format(price);
};

/**
 * Formats a low/high pair as a range, collapsing to a single number when the
 * bounds are equal (e.g. a custom usage override makes low === high). Keeps
 * the en-dash range otherwise: "500–2,000".
 */
export const formatNumberRange = (low: number, high: number, locale: string): string =>
	low === high
		? formatNumber(low, locale)
		: `${formatNumber(low, locale)}–${formatNumber(high, locale)}`;

/**
 * Price variant of {@link formatNumberRange}. Preserves the display shape of
 * the overage-cost headline: currency prefix, priced low bound, then a
 * bare-number high bound to avoid repeating the currency symbol. Collapses to
 * a single priced value when the bounds are equal.
 */
export const formatPriceRange = (low: number, high: number, options: FormatPriceOptions): string =>
	low === high
		? `${options.currency} ${formatPrice(low, options)}`
		: `${options.currency} ${formatPrice(low, options)}–${formatNumber(high, options.locale)}`;
